import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import SupportTicketForm from './SupportTicketForm';
import { RootState, useAppDispatch } from '../../../store';
import { fetchAllUserTickets } from '../../../store/actions/HelpAction';
import Table from '../../shared/TailwindComponents/Table';
import ListCountBadge from '../../shared/TailwindComponents/ListCountBadge';
import Badge from '../../shared/TailwindComponents/Badge';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { PiLifebuoyLight } from 'react-icons/pi';
import debounce from 'lodash.debounce';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, supportCenterTourSteps } from '../../shared/FeatureTour';

interface SupportCenterProps {
  searchQuery: string;
  sortValue: string;
  isCreatingTicket: boolean;
}

const SupportCenter: React.FC<SupportCenterProps> = ({ searchQuery, sortValue, isCreatingTicket }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { allUserTickets: userTickets, total } = useSelector((state: RootState) => state.help);
  const [isFetching, setIsFetching] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const ticketsPerPage = 50;
  const hasFetched = useRef(false);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'support-center',
    steps: supportCenterTourSteps,
    autoStartDelay: 600,
  });

  const getQueryParams = useCallback(() => {
    return {
      limit: ticketsPerPage,
      offset,
    };
  }, [offset, ticketsPerPage]); 

  const refetchTickets = useCallback(
    async (fetchOffset: number = 0, reset: boolean = false) => {
      if (isFetching) return;
      setIsFetching(true);
      const resp = await dispatch(fetchAllUserTickets({ ...getQueryParams(), offset: fetchOffset, reset }) as any);
      if (resp) {
        setHasMore(resp.tickets.length === ticketsPerPage && fetchOffset + ticketsPerPage < resp.total);
        setOffset(fetchOffset);
      }
      setIsFetching(false);
    },
    [dispatch, getQueryParams, ticketsPerPage, isFetching]
  );

  const loadMoreTickets = useCallback(
    debounce(async () => {
      if (!hasMore || isFetching) return;
      setIsFetching(true);
      const nextOffset = offset + ticketsPerPage;
      const resp = await dispatch(fetchAllUserTickets({ ...getQueryParams(), offset: nextOffset, reset: false }) as any);
      if (resp) {
        setOffset(nextOffset);
        setHasMore(resp.tickets.length === ticketsPerPage && nextOffset + ticketsPerPage < resp.total);
      }
      setIsFetching(false);
    }, 300),
    [dispatch, getQueryParams, hasMore, isFetching, offset, ticketsPerPage]
  );

  useEffect(() => {
    if (isCreatingTicket || isFetching) return;

    if (!hasFetched.current) {
      hasFetched.current = true;
      refetchTickets(0, true);
    }
  }, [dispatch, isCreatingTicket, isFetching, refetchTickets]);

  const handleCreateTicket = () => {
    history.push('/help/support?add=true');
  };

  const handleCreateTicketClose = () => {
    hasFetched.current = false;
    history.push('/help/support');
  };

  const getStatusColor = (status: string): 'gray' | 'green' | 'blue' | 'amber' | 'red' => {
    switch (status?.toLowerCase()) {
      case 'open': return 'blue';
      case 'in_progress': return 'amber';
      case 'closed': return 'green';
      case 'pending_user': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority: string): 'gray' | 'green' | 'blue' | 'amber' | 'red' => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'blue';
      case 'high': return 'amber';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const columns = [t('Ticket ID'), t('Title'), t('Status'), t('Priority'), t('Created At')];

  const tableData = userTickets.map((ticket: any) => ({
    id: ticket.id,
    [t('Ticket ID')]: `#${String(ticket.id).substring(0, 8).toUpperCase()}`,
    [t('Title')]: ticket.title || '',
    [t('Status')]: <Badge color={getStatusColor(ticket.status)} className='capitalize'>{ticket.status || 'Open'}</Badge>,
    [t('Priority')]: <Badge color={getPriorityColor(ticket.priority)} className='capitalize'>{ticket.priority || 'Medium'}</Badge>,
    [t('Created At')]: ticket.created_at ? format(new Date(ticket.created_at), 'yyyy-MM-dd HH:mm') : '',
  }));

  if (isCreatingTicket) {
    return (
      <div className="h-full">
        <SupportTicketForm onCancel={handleCreateTicketClose} />
      </div>
    );
  }

  return (
    <>
    <div data-tour-id="support-tickets" className="text-text-light dark:text-text-dark transition-colors duration-300 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Measured, not a second scroller: the Table below owns the only one. */}
      <div className="flex-1 min-h-0 flex flex-col">
        {isFetching ? (
          <Skeleton variant="table" rows={5} />
        ) : (
          <div className="animate-fade-in flex-1 min-h-0 flex flex-col">
            {/* No filter bar in this view - see ListCountBadge. */}
            <ListCountBadge
              showing={userTickets.length}
              total={total}
              label="tickets"
            />
            <Table
              columns={columns}
              data={tableData}
              onRowClick={(row) => history.push(`/help/support/${row.id}`)}
              hasMore={hasMore}
              isLoadingMore={isFetching}
              onLoadMore={loadMoreTickets}
              total_items={total}
              total_showing_items={userTickets.length}
              setIsShowing={true}
              showingLabel={t('tickets')}
              emptyState={{
                icon: <PiLifebuoyLight />,
                headline: searchQuery ? t('No results found.') : t('No support tickets available.'),
                subtext: searchQuery
                  ? t('Try adjusting your search to find what you’re looking for.')
                  : t('Create a ticket to report issues or request assistance from our support team.'),
                button: {
                  label: t('Create New Ticket'),
                  onClick: handleCreateTicket,
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
    <FeatureTour
      steps={supportCenterTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default SupportCenter;
